/**
 * Agent settings — what the Supervisor and the Knowledge Agent do, as plain
 * choices instead of prompt text (Bobcat prototype, "Agent settings").
 *
 * No new storage: each agent's choices are written as a managed block of plain
 * sentences into the text that agent's model already reads —
 * `otherInformation` for the Supervisor, `knowledgeAgentContext` for the
 * Knowledge Agent. Every sentence is the label of a choice, so the block reads
 * back to exactly the choices that wrote it. Anything outside the block is the
 * designer's own text and is never touched.
 *
 * AI first, not AI only: the model gets the sentences; the choices themselves
 * are fixed options, and only a person (or an applied change card) sets them.
 */

export const SUPERVISOR_CHOICES = {
  clarify: [
    'Ask which one they meant',
    'Pick the closest match',
    'Hand off to a person',
  ],
  handoffUpset: [
    'At the first sign of frustration',
    'When they ask for a person',
    'Never — keep trying',
  ],
  handoffFails: ['1', '2', '3'],
  outOfScope: [
    'Say what it can help with, then offer a person',
    'Say what it can help with',
    'Hand off to a person',
  ],
} as const;

export const KNOWLEDGE_CHOICES = {
  length: [
    'Short — two or three sentences',
    'A paragraph',
    'As long as it needs',
  ],
  noAnswer: [
    'Say it doesn’t know, then offer a person',
    'Ask them to rephrase',
    'Hand off to a person',
  ],
} as const;

type Choice<
  T extends Record<string, readonly string[]>,
  K extends keyof T,
> = T[K][number];

export interface SupervisorSettings {
  clarify: Choice<typeof SUPERVISOR_CHOICES, 'clarify'>;
  handoffAsk: boolean;
  handoffUpset: Choice<typeof SUPERVISOR_CHOICES, 'handoffUpset'>;
  handoffFails: Choice<typeof SUPERVISOR_CHOICES, 'handoffFails'>;
  outOfScope: Choice<typeof SUPERVISOR_CHOICES, 'outOfScope'>;
  chitchat: boolean;
}

export interface KnowledgeSettings {
  length: Choice<typeof KNOWLEDGE_CHOICES, 'length'>;
  cite: boolean;
  strict: boolean;
  noAnswer: Choice<typeof KNOWLEDGE_CHOICES, 'noAnswer'>;
  disclaimer: boolean;
}

export type AgentKey = 'supervisor' | 'knowledge';

/** What a solution behaves like before anyone sets anything. */
export const SUPERVISOR_DEFAULTS: SupervisorSettings = {
  clarify: 'Ask which one they meant',
  handoffAsk: true,
  handoffUpset: 'When they ask for a person',
  handoffFails: '2',
  outOfScope: 'Say what it can help with, then offer a person',
  chitchat: true,
};

export const KNOWLEDGE_DEFAULTS: KnowledgeSettings = {
  length: 'Short — two or three sentences',
  cite: true,
  strict: true,
  noAnswer: 'Say it doesn’t know, then offer a person',
  disclaimer: false,
};

export const SETTING_LABEL: Record<
  keyof SupervisorSettings | keyof KnowledgeSettings,
  string
> = {
  clarify: 'When a message fits more than one agent',
  handoffAsk: 'Hand off when they ask for a person',
  handoffUpset: 'Hand off when they’re upset',
  handoffFails: 'Hand off after failed tries',
  outOfScope: 'When it’s out of scope',
  chitchat: 'Small talk',
  length: 'Answer length',
  cite: 'Link to the source',
  strict: 'Only what the sources say',
  noAnswer: 'When it can’t find an answer',
  disclaimer: 'Say it’s an automated assistant',
};

export const formatSetting = (key: string, value: unknown): string =>
  typeof value === 'boolean'
    ? value
      ? 'On'
      : 'Off'
    : key === 'handoffFails'
      ? `${value} failed ${value === '1' ? 'try' : 'tries'}`
      : String(value);

/* ---- The managed block ---------------------------------------------------- */

const BEGIN = '[Agent settings — managed in Pro Studio; edit them there]';
const END = '[End of agent settings]';

/** `Label: value` per line — the model reads it, and it parses back exactly. */
const lines = (values: object) =>
  Object.entries(values).map(
    ([key, value]) =>
      `${SETTING_LABEL[key as keyof typeof SETTING_LABEL]}: ${formatSetting(key, value)}.`
  );

export const renderSettingsBlock = (values: object): string =>
  [BEGIN, ...lines(values), END].join('\n');

const blockPattern = () =>
  new RegExp(
    `\\n*${BEGIN.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[\\s\\S]*?${END.replace(
      /[.*+?^${}()|[\]\\]/g,
      '\\$&'
    )}\\n*`
  );

/** The text with the block replaced (or added at the end). */
export const withSettingsBlock = (
  text: string | null | undefined,
  values: object
): string => {
  const rest = (text ?? '').replace(blockPattern(), '\n\n').trim();
  const block = renderSettingsBlock(values);
  return rest ? `${rest}\n\n${block}` : block;
};

/**
 * The choices a block holds, over `defaults` — a solution with no block, or a
 * block someone edited by hand into a value that isn't a choice, falls back to
 * the default for that setting.
 */
export const parseSettingsBlock = <T extends object>(
  text: string | null | undefined,
  defaults: T,
  choices: Partial<Record<keyof T, readonly string[]>>
): { values: T; managed: boolean } => {
  const match = (text ?? '').match(blockPattern());
  if (!match) return { values: { ...defaults }, managed: false };
  const values = { ...defaults } as Record<string, unknown>;
  const byLabel = new Map(
    Object.keys(defaults).map((key) => [
      SETTING_LABEL[key as keyof typeof SETTING_LABEL],
      key,
    ])
  );
  match[0].split('\n').forEach((line) => {
    const m = line.match(/^(.+?): (.+)\.$/);
    const key = m && byLabel.get(m[1]);
    if (!m || !key) return;
    const raw = m[2];
    const current = (defaults as Record<string, unknown>)[key];
    if (typeof current === 'boolean') {
      if (raw === 'On' || raw === 'Off') values[key] = raw === 'On';
      return;
    }
    const value =
      key === 'handoffFails' ? raw.replace(/ failed tr(y|ies)$/, '') : raw;
    const allowed = choices[key as keyof T];
    if (!allowed || allowed.includes(value)) values[key] = value;
  });
  return { values: values as T, managed: true };
};

export const readSupervisorSettings = (otherInformation?: string | null) =>
  parseSettingsBlock(otherInformation, SUPERVISOR_DEFAULTS, SUPERVISOR_CHOICES);

export const readKnowledgeSettings = (knowledgeAgentContext?: string | null) =>
  parseSettingsBlock(
    knowledgeAgentContext,
    KNOWLEDGE_DEFAULTS,
    KNOWLEDGE_CHOICES
  );

/** The designer's own text, without the managed block — for showing it. */
export const withoutSettingsBlock = (text: string | null | undefined) =>
  (text ?? '').replace(blockPattern(), '\n\n').trim();
